import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";
import KeyIcon from "@mui/icons-material/Key";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockIcon from "@mui/icons-material/Lock";
import RouteIcon from "@mui/icons-material/Route";
import { useNavigate } from "react-router-dom";

const C = {
  bg: "#070a12",
  surface: "#101521",
  surface2: "#151c2b",
  border: "rgba(255,255,255,0.10)",
  borderStrong: "rgba(255,255,255,0.18)",
  white: "#ffffff",
  text: "rgba(255,255,255,0.84)",
  muted: "rgba(255,255,255,0.62)",
  faint: "rgba(255,255,255,0.42)",
  accent: "#2af598",
  accentDim: "rgba(42,245,152,0.12)",
  accentBorder: "rgba(42,245,152,0.32)",
  warn: "#f5c518",
  warnDim: "rgba(245,197,24,0.10)",
  warnBorder: "rgba(245,197,24,0.34)",
  danger: "#ff5b70",
};

const LAYERS = [
  {
    num: "01",
    title: "API Keys & Exchange Credentials",
    icon: <KeyIcon />,
    status: "Operational",
    color: "#ff7043",
    summary: "Secrets are isolated from application code and injected only at Cloud Function runtime.",
    items: [
      ["Key Storage", "Google Cloud Secret Manager. Never hardcoded, never stored in Firestore, never committed to the repository."],
      ["Runtime Injection", "Cloud Functions receive keys through secret bindings only when the function runs."],
      ["Exchange Restrictions", "Bitget and Bybit accounts should be locked to approved Firebase/Cloud Run IPs."],
      ["Scope Minimisation", "Read-only permissions where possible. Withdrawal permissions limited to approved flows."],
      ["Rotation Policy", "Rotate every 90 days or immediately after any suspected compromise."],
    ],
  },
  {
    num: "02",
    title: "Liquidity Wallet Segmentation",
    icon: <AccountBalanceIcon />,
    status: "Partial",
    color: C.warn,
    summary: "Operational liquidity stays separated from treasury funds to reduce exposure.",
    items: [
      ["Hot Wallet Principle", "The operational wallet should only hold the minimum required for the current payment batch."],
      ["Treasury Isolation", "Primary client funds stay in cold exchange custody or future multi-sig treasury controls."],
      ["Insufficient Balance Gate", "If operational liquidity is insufficient, the payment must move to manual review."],
      ["Daily Exposure Cap", "fund_rules/active -> auto_pay.daily_cap limits how much can be paid automatically each day."],
      ["Recommended Upgrade", "Hardware wallet plus Gnosis Safe multi-sig for treasury-level operations."],
    ],
  },
  {
    num: "03",
    title: "Wallet Security Cooldowns",
    icon: <AccessTimeIcon />,
    status: "Operational",
    color: "#42a5f5",
    summary: "New and edited wallets are temporarily blocked from auto-pay to reduce fraud risk.",
    items: [
      ["New Wallet Hold", "24-hour cooldown via auto_pay_eligible_from. Auto-pay skips wallets inside the hold period."],
      ["Post-Edit Suspension", "24-hour suspension via edit_suspended_until after every wallet edit."],
      ["Address Immutability", "Firestore rules prevent wallet address mutation after creation."],
      ["Audit Trail", "edit_history array plus wallet_audit_log collection tracks creation and edits."],
      ["Admin Notification", "Wallet creation and edits generate internal admin alerts."],
    ],
  },
  {
    num: "04",
    title: "Transaction Safeguards",
    icon: <VerifiedUserIcon />,
    status: "Operational",
    color: C.accent,
    summary: "Deposits and withdrawals pass through explicit gates before balances or payments are finalized.",
    items: [
      ["Deposit Approval Gate", "Funds are credited only after admin review and approval."],
      ["Withdrawal Threshold", "High-value withdrawals bypass automatic processing and require manual decision."],
      ["Daily Volume Cap", "Auto-pay stops once the configured daily cap is reached."],
      ["Expiry Window", "Withdrawal requests expire from auto-pay if not accepted within the configured max window (hours)."],
      ["On-Chain Execution", "Payment execution remains manual until exchange API integration is live."],
    ],
  },
  {
    num: "05",
    title: "Access Control & Audit Logging",
    icon: <AdminPanelSettingsIcon />,
    status: "Operational",
    color: "#ab47bc",
    summary: "Admin access, actions, and critical mutations are restricted and traceable.",
    items: [
      ["Authentication", "Firebase Authentication with approved admin UID access."],
      ["Firestore Rules", "Role-based restrictions applied server-side."],
      ["Action Logging", "Critical admin operations include operator email and timestamp."],
      ["Internal Alerts", "Large deposits, high-value withdrawals, wallet creation, and edits send admin emails."],
      ["Next Control", "Dual approval for high-value transactions should be added in the next security phase."],
    ],
  },
];

const FLOW = [
  ["1", "Client submits withdrawal request in the app", "#42a5f5"],
  ["2", "System waits accept_delay and validates amount, wallet status, and cooldowns", "#42a5f5"],
  ["3", "New or recently edited wallet is blocked and routed to manual review", C.warn],
  ["4", "Daily cap and liquidity checks decide whether the flow can continue", C.warn],
  ["5", "After pay_delay, transaction becomes ready-to-pay and admin is notified", C.accent],
  ["6", "Admin executes on-chain payment manually until exchange API integration is ready", C.danger],
];

const STATUS = [
  ["Google Cloud Secret Manager", true, "Secrets are separated from code."],
  ["Admin email alerts", true, "Wallet changes, large deposits, and high-value withdrawals trigger alerts."],
  ["Wallet cooldowns", true, "New/edited wallets are blocked from immediate auto-pay."],
  ["Firestore address immutability", true, "Wallet address cannot be changed after creation."],
  ["Exchange IP allowlist", false, "Needs confirmation inside Bitget/Bybit dashboards."],
  ["Exchange API auto-pay", false, "Not implemented. Current payment step is manual."],
  ["Hardware wallet / multi-sig treasury", false, "Recommended for treasury-level controls."],
];

const SCORE = Math.round((STATUS.filter(([, ok]) => ok).length / STATUS.length) * 100);

const SectionLabel = ({ children }) => (
  <Typography sx={{ color: C.faint, fontSize: 13, fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase", mb: 2 }}>
    {children}
  </Typography>
);

const MetricCard = ({ label, value, detail, icon, color = C.accent }) => (
  <Card sx={{ height: "100%", bgcolor: "rgba(255,255,255,0.045)", border: `1px solid ${C.border}`, borderRadius: 4 }}>
    <CardContent sx={{ p: 3.25 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography sx={{ color: C.muted, fontSize: 13, fontWeight: 900, letterSpacing: "0.14em", textTransform: "uppercase" }}>
          {label}
        </Typography>
        <Box sx={{ color, display: "flex", "& svg": { fontSize: 26 } }}>{icon}</Box>
      </Stack>
      <Typography sx={{ color: C.white, fontSize: { xs: 36, md: 44 }, fontWeight: 950, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography sx={{ color: C.muted, fontSize: 16, lineHeight: 1.6, mt: 1.35 }}>
        {detail}
      </Typography>
    </CardContent>
  </Card>
);

const LayerCard = ({ layer }) => (
  <Card
    sx={{
      mb: 3,
      bgcolor: C.surface,
      background: `linear-gradient(135deg, ${C.surface} 0%, ${C.surface2} 100%)`,
      border: `1px solid ${C.borderStrong}`,
      borderTop: `4px solid ${layer.color}`,
      borderRadius: 4,
      overflow: "hidden",
      boxShadow: "0 22px 60px rgba(0,0,0,0.28)",
    }}
  >
    <CardContent sx={{ p: 0 }}>
      <Box sx={{ p: { xs: 2.5, md: 3.5 }, borderBottom: `1px solid ${C.border}` }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2}>
          <Stack direction="row" spacing={2.25} alignItems="center">
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: 3,
                bgcolor: `${layer.color}18`,
                border: `1px solid ${layer.color}45`,
                color: layer.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "& svg": { fontSize: 32 },
              }}
            >
              {layer.icon}
            </Box>
            <Box>
              <Typography sx={{ color: layer.color, fontSize: 13, fontWeight: 900, letterSpacing: "0.16em" }}>
                LAYER {layer.num}
              </Typography>
              <Typography sx={{ color: C.white, fontSize: { xs: 22, md: 26 }, fontWeight: 950, lineHeight: 1.18 }}>
                {layer.title}
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={layer.status}
            sx={{
              color: layer.status === "Operational" ? C.accent : C.warn,
              bgcolor: layer.status === "Operational" ? C.accentDim : C.warnDim,
              border: `1px solid ${layer.status === "Operational" ? C.accentBorder : C.warnBorder}`,
              fontWeight: 900,
              height: 36,
              px: 1,
            }}
          />
        </Stack>
        <Typography sx={{ color: C.text, fontSize: 17, lineHeight: 1.65, mt: 2 }}>
          {layer.summary}
        </Typography>
      </Box>

      <Grid container>
        {layer.items.map(([label, detail], index) => (
          <Grid item xs={12} md={index === layer.items.length - 1 ? 12 : 6} key={label}>
            <Box
              sx={{
                minHeight: 126,
                p: { xs: 2.5, md: 3 },
                borderRight: { md: index % 2 === 0 && index !== layer.items.length - 1 ? `1px solid ${C.border}` : "none" },
                borderBottom: index < layer.items.length - 1 ? `1px solid ${C.border}` : "none",
                "&:hover": { bgcolor: "rgba(255,255,255,0.025)" },
              }}
            >
              <Typography sx={{ color: layer.color, fontSize: 16, fontWeight: 900, mb: 1 }}>
                {label}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: 16, lineHeight: 1.65 }}>
                {detail}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </CardContent>
  </Card>
);

const SecurityArchitecture = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: C.bg, color: C.white, pb: 6 }}>
      <Box
        sx={{
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 6 },
          borderBottom: `1px solid ${C.borderStrong}`,
          background:
            "radial-gradient(circle at 15% 20%, rgba(42,245,152,0.16) 0%, transparent 34%), radial-gradient(circle at 85% 5%, rgba(66,165,245,0.14) 0%, transparent 28%), linear-gradient(135deg, #07111a 0%, #070a12 75%)",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "flex-start" }} spacing={3}>
          <Box sx={{ maxWidth: 920 }}>
            <Stack direction="row" spacing={2.25} alignItems="center" sx={{ mb: 2.5 }}>
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: 4,
                  bgcolor: C.accentDim,
                  border: `1px solid ${C.accentBorder}`,
                  boxShadow: "0 0 48px rgba(42,245,152,0.14)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShieldIcon sx={{ color: C.accent, fontSize: 38 }} />
              </Box>
              <Box>
                <Typography sx={{ color: C.accent, fontSize: 13, fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                  Fiddux Admin Platform - Internal Security Control
                </Typography>
                <Typography sx={{ color: C.white, fontSize: { xs: 34, md: 54 }, fontWeight: 950, lineHeight: 1.03 }}>
                  Security Architecture
                </Typography>
              </Box>
            </Stack>
            <Typography sx={{ color: "rgba(255,255,255,0.76)", fontSize: { xs: 17, md: 20 }, lineHeight: 1.65 }}>
              Executive view of how Fiddux protects operational wallets, admin actions,
              high-value withdrawals, exchange credentials, and treasury exposure.
            </Typography>
          </Box>

          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin/system-health")}
            sx={{
              color: C.white,
              border: `1px solid ${C.borderStrong}`,
              borderRadius: 2,
              px: 2.75,
              py: 1.25,
              fontSize: 15,
              bgcolor: "rgba(255,255,255,0.04)",
              "&:hover": { borderColor: C.accentBorder, bgcolor: "rgba(42,245,152,0.08)" },
            }}
          >
            Back to System Health
          </Button>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 3, md: 5 }, py: 4 }}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <MetricCard label="Security Score" value={`${SCORE}%`} detail="Implemented controls currently active or documented in the admin platform." icon={<LockIcon />} />
          </Grid>
          <Grid item xs={12} md={4}>
            <MetricCard label="Defence Layers" value="5" detail="Credentials, liquidity, cooldowns, transaction gates, and audit controls." color="#42a5f5" icon={<ShieldIcon />} />
          </Grid>
          <Grid item xs={12} md={4}>
            <MetricCard label="Manual Risk Gates" value="3" detail="Large withdrawals, exchange execution, and treasury movement remain manually gated." color={C.warn} icon={<WarningAmberIcon />} />
          </Grid>
        </Grid>

        <Grid container spacing={4}>
          <Grid item xs={12} xl={8}>
            <SectionLabel>Defence Layers</SectionLabel>
            {LAYERS.map((layer) => (
              <LayerCard key={layer.num} layer={layer} />
            ))}
          </Grid>

          <Grid item xs={12} xl={4}>
            <Box sx={{ position: { xl: "sticky" }, top: 24 }}>
              <Card sx={{ bgcolor: C.surface, border: `1px solid ${C.borderStrong}`, borderRadius: 4, mb: 3, overflow: "hidden" }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: 3, borderBottom: `1px solid ${C.border}`, bgcolor: "rgba(255,255,255,0.025)" }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <RouteIcon sx={{ color: C.accent, fontSize: 30 }} />
                      <Box>
                        <SectionLabel>Auto-Pay Withdrawal Flow</SectionLabel>
                        <Typography sx={{ color: C.muted, fontSize: 15, lineHeight: 1.5, mt: -1 }}>
                          Funds only leave after multiple operational gates.
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                  <Stack sx={{ p: 3 }} spacing={0}>
                    {FLOW.map(([step, label, color], index) => (
                      <Box key={step} sx={{ display: "flex", gap: 2 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: "50%",
                              bgcolor: `${color}18`,
                              border: `2px solid ${color}70`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography sx={{ color, fontWeight: 950, fontSize: 17 }}>{step}</Typography>
                          </Box>
                          {index < FLOW.length - 1 && (
                            <Box sx={{ width: 2, minHeight: 34, flexGrow: 1, bgcolor: `${color}32`, my: 0.75 }} />
                          )}
                        </Box>
                        <Typography sx={{ color: C.text, fontSize: 16.5, lineHeight: 1.65, pt: 0.9, pb: index < FLOW.length - 1 ? 2 : 0 }}>
                          {label}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ bgcolor: C.surface, border: `1px solid ${C.borderStrong}`, borderRadius: 4, overflow: "hidden" }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: 3, borderBottom: `1px solid ${C.border}`, bgcolor: "rgba(255,255,255,0.025)" }}>
                    <SectionLabel>Implementation Status</SectionLabel>
                    <Typography sx={{ color: C.muted, fontSize: 15, lineHeight: 1.5, mt: -1 }}>
                      Live status of the controls that matter most.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ color: C.white, fontSize: 30, fontWeight: 950, mb: 1 }}>{SCORE}% complete</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={SCORE}
                        sx={{
                          height: 11,
                          borderRadius: 20,
                          bgcolor: "rgba(255,255,255,0.08)",
                          "& .MuiLinearProgress-bar": { bgcolor: C.accent, borderRadius: 20 },
                        }}
                      />
                    </Box>
                  </Box>

                  <Stack spacing={1.25} sx={{ p: 2.5 }}>
                    {STATUS.map(([label, ok, note]) => (
                      <Box
                        key={label}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                          p: 1.75,
                          borderRadius: 2,
                          bgcolor: ok ? "rgba(42,245,152,0.045)" : C.warnDim,
                          border: `1px solid ${ok ? "rgba(42,245,152,0.16)" : C.warnBorder}`,
                        }}
                      >
                        {ok ? (
                          <CheckCircleIcon sx={{ color: C.accent, fontSize: 23, mt: 0.15 }} />
                        ) : (
                          <WarningAmberIcon sx={{ color: C.warn, fontSize: 23, mt: 0.15 }} />
                        )}
                        <Box>
                          <Typography sx={{ color: ok ? C.white : C.warn, fontSize: 16, fontWeight: 800, lineHeight: 1.35 }}>
                            {label}
                          </Typography>
                          <Typography sx={{ color: C.muted, fontSize: 14.5, lineHeight: 1.5, mt: 0.25 }}>
                            {note}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>

              <Stack direction={{ xs: "column", sm: "row", xl: "column" }} spacing={2} sx={{ mt: 3 }}>
                <Button
                  fullWidth
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate("/admin/auto-pay-flow")}
                  sx={{
                    bgcolor: C.accent,
                    color: "#000",
                    fontWeight: 800,
                    fontSize: 15,
                    py: 1.35,
                    "&:hover": { bgcolor: "#1de585" },
                  }}
                >
                  Open detailed Auto-Pay flow
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate("/admin/admin-settings")}
                  sx={{ color: C.white, borderColor: C.borderStrong, fontSize: 15, py: 1.35, "&:hover": { borderColor: C.accentBorder, bgcolor: "rgba(42,245,152,0.06)" } }}
                >
                  Configure Admin Settings
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate("/admin/wallet-management")}
                  sx={{ color: C.white, borderColor: C.borderStrong, fontSize: 15, py: 1.35, "&:hover": { borderColor: C.accentBorder, bgcolor: "rgba(42,245,152,0.06)" } }}
                >
                  Review Wallet Management
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default SecurityArchitecture;
